import aiMassiva from 'dashboard/api/aiMassiva';
import aiNetworkOutages from 'dashboard/api/aiNetworkOutages';
import aiNetworkIncidentNotices from 'dashboard/api/aiNetworkIncidentNotices';
import aiNetworkMaintenances from 'dashboard/api/aiNetworkMaintenances';

// Estado da tela de Massiva. DUAS fontes, de propósito (D6 do
// .ai/massiva-arquitetura.md):
//
//   snapshot → compute ao vivo no ERP (10-45s): hero, KPIs, mapa da rede.
//   outages  → Postgres (ms): tabela do ciclo de vida + linha do tempo.
//
// Elas carregam INDEPENDENTES: o histórico já está no banco e não tem por que
// esperar a varredura do ERP pra aparecer.
export const types = {
  SET_UI_FLAG: 'SET_MASSIVA_UI_FLAG',
  SET_SNAPSHOT: 'SET_MASSIVA_SNAPSHOT',
  SET_OUTAGES: 'SET_MASSIVA_OUTAGES',
  SET_OPEN_OUTAGES: 'SET_MASSIVA_OPEN_OUTAGES',
  SET_NOTICE_CONFIG: 'SET_MASSIVA_NOTICE_CONFIG',
  SET_META: 'SET_MASSIVA_META',
  SET_TIMELINE: 'SET_MASSIVA_TIMELINE',
  SET_ERROR: 'SET_MASSIVA_ERROR',
  SET_FILTERS: 'SET_MASSIVA_FILTERS',
  SET_CLIENTS: 'SET_MASSIVA_CLIENTS',
  SET_SELECTED: 'SET_MASSIVA_SELECTED',
  ADD_PENDING: 'ADD_MASSIVA_PENDING',
  CLEAR_PENDING: 'CLEAR_MASSIVA_PENDING',
  SET_NOTICES: 'SET_MASSIVA_NOTICES',
  SET_MAINTENANCE_AUDIENCE: 'SET_MASSIVA_MAINTENANCE_AUDIENCE',
};

// A tela abre no que está em queda AGORA: o histórico é consulta, não o assunto
// de quem abre o painel.
const initialFilters = () => ({
  status: 'open',
  sort: 'detected_at',
  order: 'desc',
  page: 1,
  perPage: 25,
  q: '',
  // Filtro por instância (card da faixa). PRECISA existir aqui: SET_FILTERS é
  // merge — chave ausente do initialFilters sobreviveria ao clearFilters e
  // viraria filtro fantasma que nenhum botão limpa.
  systemLabel: '',
});

// A linha do tempo é visão fixa (as mais recentes, abertas + fechadas) — não
// segue status/página/busca da tabela, senão o Gantt mudaria de assunto a cada
// clique. O ESCOPO de instância (systemLabel) ela segue: com filtro ativo, um
// Gantt de outra instância seria exatamente o "mudar de assunto" que se evita.
const TIMELINE_SIZE = 50;

const state = {
  snapshot: null,
  outages: [],
  // O conjunto COMPLETO de massivas abertas — fonte da árvore caixa→clientes
  // e do seletor de escopo dos comunicados. Separado de `outages` (a tabela)
  // de propósito: a tabela é paginada (25/pág) e carrega filtros persistidos
  // do dashboard — derivar a árvore dela mostrava "rede normalizou" falso.
  openOutages: [],
  meta: { totalCount: 0, page: 1, perPage: 25 },
  timeline: [],
  // Lista congelada de afetados, por massiva. Cacheada: reabrir o drawer da
  // mesma massiva não refaz request.
  clientsByOutage: {},
  // Público congelado por manutenção, pra árvore caixa→clientes dos comunicados
  // `de_manutencao` na página de Comunicados. Cacheado: reabrir o mesmo card
  // não refaz o GET de público. { [id]: { isLoading, error, data } }
  audienceByMaintenance: {},
  selectedOutageId: null,
  // Comunicados de rede: { ativos: [], encerrados: [] }. Vive FORA do mapa —
  // o grafo some quando a rede normaliza, o comunicado não pode sumir junto
  // (ele segue no ar até a validade/quarentena/encerramento manual).
  notices: { ativos: [] },
  // Transições que o poll fez enquanto o operador lia fora do topo da lista.
  pendingEvents: 0,
  // Erro da LISTA. Enquanto tiver conteúdo, a tabela some e o banner aparece:
  // "0 massivas" só é dito quando o backend realmente disse isso.
  error: '',
  filters: initialFilters(),
  uiFlags: {
    fetchingSnapshot: false,
    // Pedido de ciclo prioritário em voo (POST /refresh → produtor MCP): o
    // botão Atualizar trava até TODAS as instâncias pedidas reportarem
    // (snapshot_written por system_label) ou o timeout de segurança soltar.
    refreshRequested: false,
    fetchingList: false,
    fetchingOpenOutages: false,
    fetchingTimeline: false,
  },
};

const getters = {
  getSnapshot: $state => $state.snapshot,
  getNotices: $state => $state.notices,
  getOutages: $state => $state.outages,
  getOpenOutages: $state => $state.openOutages,
  getMeta: $state => $state.meta,
  getTimeline: $state => $state.timeline,
  getFilters: $state => $state.filters,
  getError: $state => $state.error,
  getPendingEvents: $state => $state.pendingEvents,
  getUIFlags: $state => $state.uiFlags,
  getSelectedOutage: $state =>
    $state.outages.find(o => o.id === $state.selectedOutageId) || null,
  // { isLoading, error, clients } — o drawer lê daqui, nunca busca sozinho.
  getSelectedClients: $state =>
    $state.clientsByOutage[$state.selectedOutageId] || {
      isLoading: true,
      error: '',
      clients: [],
    },
  // O MAPA inteiro por massiva — a árvore de escopo dos comunicados lê clientes
  // de VÁRIAS caixas ao mesmo tempo (não só a selecionada do drawer).
  getClientsByOutage: $state => $state.clientsByOutage,
  getAudienceByMaintenance: $state => $state.audienceByMaintenance,
};

const apiErrorMessage = error =>
  error.response?.data?.error || error.message || '';

// Releitura cache-only (tick do poll): instância sem retrato fresco volta
// snapshot=null — preserva o último estado exibido (inclusive o degraded do
// mount) em vez de apagar a instância da tela.
const mergeSnapshot = (prev, next) => {
  if (!prev?.instances?.length || !next?.instances) return next;
  const prevByLabel = new Map(
    prev.instances.map(inst => [inst.system_label, inst])
  );
  return {
    ...next,
    instances: next.instances.map(inst =>
      inst.snapshot
        ? inst
        : {
            ...inst,
            snapshot: prevByLabel.get(inst.system_label)?.snapshot ?? null,
          }
    ),
  };
};

const isAborted = error =>
  error.code === 'ERR_CANCELED' || error.name === 'CanceledError';

// Guarda de corrida: só a resposta do ÚLTIMO pedido pode escrever no estado. O
// operador troca sort/página mais rápido do que o servidor responde.
let listController = null;
let listSeq = 0;

// Um ciclo do poll transiciona VÁRIAS massivas de uma vez (um corte de fibra
// derruba N caixas juntas) e cada uma emite seu evento — sem agrupar, um poll
// vira uma rajada de refetches.
let refreshTimer = null;
// Idem pro retrato: conta com N instâncias emite um snapshot_written por
// instância no mesmo ciclo — agrupa numa releitura só.
let snapshotTimer = null;
// Solta o botão Atualizar se o produtor não responder (instância lenta cicla
// em até ~2min; além disso é anomalia e o operador pode pedir de novo).
let refreshSafetyTimer = null;
// Instâncias cujo ciclo prioritário foi pedido e ainda não reportou: o botão
// só solta quando o snapshot_written de CADA uma chegar — o retrato de
// ROTINA de outra instância não pode soltar o botão antes da hora.
let pendingRefreshLabels = new Set();
const REFRESH_DEBOUNCE = 600;
const REFRESH_SAFETY_MS = 150000;

const actions = {
  // ── Comunicados de rede (incidente reativo) ───────────────────────────────
  // Falha NÃO derruba o painel (o mapa/tabela seguem): o erro sobe pro
  // chamador, que decide o toast — mas a lista fica como estava.
  async fetchNotices({ commit, rootGetters }) {
    const { data } = await aiNetworkIncidentNotices.list(
      rootGetters.getCurrentAccountId
    );
    commit(types.SET_NOTICES, data);
    return data;
  },

  async createNotice({ dispatch, rootGetters }, payload) {
    const { data } = await aiNetworkIncidentNotices.register(
      rootGetters.getCurrentAccountId,
      payload
    );
    await dispatch('fetchNotices');
    return data;
  },

  // Editar: "era 18h, não vai dar" — o prazo estica e o texto muda, sem
  // perder o público que já está sendo atendido com este comunicado.
  async updateNotice({ dispatch, rootGetters }, { id, ...payload }) {
    const { data } = await aiNetworkIncidentNotices.edit(
      rootGetters.getCurrentAccountId,
      id,
      payload
    );
    await dispatch('fetchNotices');
    return data;
  },

  // Encerramento MANUAL: o operador que falou com o técnico e sabe que acabou
  // não espera a quarentena.
  async clearNotice({ dispatch, rootGetters }, id) {
    await aiNetworkIncidentNotices.clear(rootGetters.getCurrentAccountId, id);
    await dispatch('fetchNotices');
  },

  // Janela da quarentena (da CONTA): o valor confirmado pelo PATCH entra no
  // estado NA HORA. O refetch (chips "encerra em Xmin" dos comunicados em
  // contagem) é best-effort: falha DELE não pode virar "não foi possível
  // salvar" de um save que já valeu — o tick do poll (~2min) realinha sozinho.
  async updateNoticeConfig({ commit, dispatch, rootGetters }, payload) {
    const { data } = await aiNetworkIncidentNotices.updateConfig(
      rootGetters.getCurrentAccountId,
      payload
    );
    commit(types.SET_NOTICE_CONFIG, data.config);
    dispatch('fetchNotices').catch(() => {});
    return data;
  },

  // Snapshot: hero, KPIs, mapa. SÓ-LEITURA: o backend serve sempre o último
  // retrato do produtor (stream) — instantâneo, nunca computa. `cachedOnly`
  // (tick do realtime) preserva o último estado das instâncias sem retrato
  // (merge). O `generated_at`/`snapshot_written_at` dizem de quando é a
  // leitura; forçar leitura nova é o requestRefresh.
  async fetchSnapshot({ commit, state: $state }, { cachedOnly = false } = {}) {
    commit(types.SET_UI_FLAG, { fetchingSnapshot: true });
    try {
      const { data } = await aiMassiva.get();
      commit(
        types.SET_SNAPSHOT,
        cachedOnly ? mergeSnapshot($state.snapshot, data) : data
      );
    } finally {
      commit(types.SET_UI_FLAG, { fetchingSnapshot: false });
    }
  },

  // Botão Atualizar: PEDE um ciclo prioritário ao produtor (202) — o retrato
  // novo chega pelo broadcast snapshot_written de sempre, que solta o botão.
  // Devolve `requested` pro caller avisar quando a conta não tem instância
  // no produtor (massiva não habilitado) em vez de girar pra sempre.
  async requestRefresh({ commit }) {
    const { data } = await aiMassiva.requestRefresh();
    if (data.requested > 0) {
      pendingRefreshLabels = new Set(data.system_labels || []);
      commit(types.SET_UI_FLAG, { refreshRequested: true });
      clearTimeout(refreshSafetyTimer);
      refreshSafetyTimer = setTimeout(() => {
        pendingRefreshLabels.clear();
        commit(types.SET_UI_FLAG, { refreshRequested: false });
      }, REFRESH_SAFETY_MS);
    }
    return data.requested;
  },

  async fetchOutages({ commit, state: $state, rootGetters }) {
    if (listController) listController.abort();
    listController = new AbortController();
    listSeq += 1;
    const seq = listSeq;

    commit(types.SET_UI_FLAG, { fetchingList: true });
    commit(types.SET_ERROR, '');
    try {
      const { data } = await aiNetworkOutages.list(
        rootGetters.getCurrentAccountId,
        // busca vazia não vai como `q=` na URL (ruído)
        { ...$state.filters, q: $state.filters.q || undefined },
        { signal: listController.signal }
      );
      if (seq !== listSeq) return;
      // Contrato quebrado vira BANNER, não render vazio (que o operador leria
      // como "nenhuma massiva") nem crash de template.
      if (!Array.isArray(data.payload) || !data.meta) {
        throw new Error(
          'malformed response: expected { payload: [], meta: {} }'
        );
      }
      commit(types.SET_OUTAGES, data.payload);
      commit(types.SET_META, data.meta);
      commit(types.CLEAR_PENDING); // a tela acabou de alcançar o banco
    } catch (error) {
      if (isAborted(error) || seq !== listSeq) return;
      commit(types.SET_OUTAGES, []);
      commit(types.SET_ERROR, apiErrorMessage(error));
      throw error;
    } finally {
      if (seq === listSeq) commit(types.SET_UI_FLAG, { fetchingList: false });
    }
  },

  // Todas as massivas ABERTAS, sem os filtros/página da tabela (ver comentário
  // do state.openOutages). Pagina até o total — o backend cabe 100/pág e
  // aberta simultânea raramente passa de uma página.
  async fetchOpenOutages({ commit, rootGetters }) {
    commit(types.SET_UI_FLAG, { fetchingOpenOutages: true });
    try {
      const accountId = rootGetters.getCurrentAccountId;
      const all = [];
      let page = 1;
      let total = Infinity;
      while (all.length < total) {
        // sequencial de propósito: o total só é conhecido após a 1ª página
        // eslint-disable-next-line no-await-in-loop
        const { data } = await aiNetworkOutages.list(accountId, {
          status: 'open',
          page,
          perPage: 100,
          sort: 'detected_at',
          order: 'desc',
        });
        if (!Array.isArray(data.payload) || !data.meta) {
          throw new Error(
            'malformed response: expected { payload: [], meta: {} }'
          );
        }
        all.push(...data.payload);
        total = Number(data.meta.total_count || 0);
        if (!data.payload.length) break;
        page += 1;
      }
      commit(types.SET_OPEN_OUTAGES, all);
      return all;
    } finally {
      commit(types.SET_UI_FLAG, { fetchingOpenOutages: false });
    }
  },

  async fetchTimeline({ commit, state: $state, rootGetters }) {
    commit(types.SET_UI_FLAG, { fetchingTimeline: true });
    try {
      const { data } = await aiNetworkOutages.list(
        rootGetters.getCurrentAccountId,
        {
          status: 'all',
          page: 1,
          perPage: TIMELINE_SIZE,
          sort: 'detected_at',
          order: 'desc',
          systemLabel: $state.filters.systemLabel,
        }
      );
      commit(types.SET_TIMELINE, data.payload || []);
    } finally {
      commit(types.SET_UI_FLAG, { fetchingTimeline: false });
    }
  },

  // Sob demanda, ao abrir o drawer. Cacheada por massiva; a entrada com erro
  // fica no cache mostrando o banner, e o retry a apaga antes de refazer.
  async fetchClients({ commit, state: $state, rootGetters }, outageId) {
    const cached = $state.clientsByOutage[outageId];
    if (cached && !cached.error) return;

    commit(types.SET_CLIENTS, {
      outageId,
      entry: { isLoading: true, error: '', clients: [] },
    });
    try {
      const { data } = await aiNetworkOutages.clients(
        rootGetters.getCurrentAccountId,
        outageId
      );
      if (!Array.isArray(data.payload)) {
        throw new Error('malformed response: expected { payload: [] }');
      }
      commit(types.SET_CLIENTS, {
        outageId,
        entry: { isLoading: false, error: '', clients: data.payload },
      });
    } catch (error) {
      // Erro fica VISÍVEL: lista vazia aqui seria lida como "ninguém foi afetado".
      commit(types.SET_CLIENTS, {
        outageId,
        entry: { isLoading: false, error: apiErrorMessage(error), clients: [] },
      });
      throw error;
    }
  },

  selectOutage({ commit, dispatch }, outageId) {
    commit(types.SET_SELECTED, outageId);
    if (outageId) dispatch('fetchClients', outageId);
  },

  retryClients({ commit, dispatch }, outageId) {
    commit(types.SET_CLIENTS, { outageId, entry: null }); // limpa pra refazer
    dispatch('fetchClients', outageId);
  },

  // Público congelado da manutenção, sob demanda (ao expandir o card
  // `de_manutencao`). Cacheado por id; a entrada com erro fica no cache
  // mostrando o banner, e um novo pedido a apaga antes de refazer.
  async fetchMaintenanceAudience({ commit, state: $state, rootGetters }, id) {
    const cached = $state.audienceByMaintenance[id];
    if (cached && !cached.error) return;

    commit(types.SET_MAINTENANCE_AUDIENCE, {
      id,
      entry: { isLoading: true, error: '', data: null },
    });
    try {
      const { data } = await aiNetworkMaintenances.audience(
        rootGetters.getCurrentAccountId,
        id
      );
      commit(types.SET_MAINTENANCE_AUDIENCE, {
        id,
        entry: { isLoading: false, error: '', data },
      });
    } catch (error) {
      // Erro VISÍVEL: público vazio aqui leria como "manutenção não afeta ninguém".
      commit(types.SET_MAINTENANCE_AUDIENCE, {
        id,
        entry: { isLoading: false, error: apiErrorMessage(error), data: null },
      });
      throw error;
    }
  },

  // Filtro/sort/busca voltam pra página 1: continuar na 5 depois de filtrar
  // cairia num vazio que o operador leria como "não tem nada".
  setFilters({ commit, dispatch }, partial) {
    const resetsPage = !('page' in partial);
    commit(types.SET_FILTERS, resetsPage ? { ...partial, page: 1 } : partial);
    dispatch('fetchOutages');
    // A timeline segue SÓ o escopo de instância — religar no setFilters
    // inteiro dobraria o custo de cada sort/página sem mudar o Gantt.
    if ('systemLabel' in partial) dispatch('fetchTimeline');
  },

  // Limpa só os filtros DA TABELA (status/busca/ordenação/página). O filtro
  // de instância é da PÁGINA (card da faixa) e sobrevive: quem o desliga é o
  // chip ✕ ou re-clicar o card — o botão da tabela tirando a instância
  // selecionada lia como bug.
  clearFilters({ commit, dispatch, state: $state }) {
    commit(types.SET_FILTERS, {
      ...initialFilters(),
      status: 'all',
      systemLabel: $state.filters.systemLabel,
    });
    dispatch('fetchOutages');
  },

  applyPending({ commit, dispatch }) {
    commit(types.SET_FILTERS, { page: 1 });
    commit(types.CLEAR_PENDING);
    dispatch('fetchOutages');
  },

  // Broadcast do Rails (massiva.event): o poll abriu/atualizou/fechou uma
  // massiva. O evento é só o AVISO — o endpoint continua a fonte de verdade.
  handleRealtimeEvent(
    { commit, dispatch, state: $state, rootGetters },
    payload
  ) {
    if (payload?.account_id !== rootGetters.getCurrentAccountId) return;

    // O poll gravou retrato novo no cache (todo ciclo, ~2min): relê SÓ o
    // snapshot em modo cache-only — instância sem retrato (ex.: degraded, que
    // o cache recusa) volta nula e o merge preserva o último estado, NUNCA
    // dispara o compute ao vivo de 10-45s (o gate do broadcast é por
    // instância; o GET é da conta toda). NÃO é transição: não refaz
    // tabela/timeline nem conta como novidade (o aviso mentiria a cada poll).
    // Falha da releitura fica pro próximo tick; o Atualizar manual toasta.
    if (payload?.event_type === 'snapshot_written') {
      // O pedido do Atualizar só está atendido quando TODAS as instâncias
      // pedidas reportarem — retrato de rotina de outra não solta o botão.
      if (
        pendingRefreshLabels.delete(payload.system_label) &&
        pendingRefreshLabels.size === 0
      ) {
        clearTimeout(refreshSafetyTimer);
        commit(types.SET_UI_FLAG, { refreshRequested: false });
      }
      clearTimeout(snapshotTimer);
      snapshotTimer = setTimeout(() => {
        dispatch('fetchSnapshot', { cachedOnly: true }).catch(() => {});
      }, REFRESH_DEBOUNCE);
      return;
    }

    // Recarregar debaixo de quem está lendo é pior que atrasar: só entra sozinho
    // quando o operador está no topo da lista e sem drawer aberto. Fora disso,
    // vira um aviso que ele aplica quando quiser.
    const atHead = $state.filters.page === 1 && !$state.selectedOutageId;
    if (!atHead) commit(types.ADD_PENDING);

    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      dispatch('fetchTimeline'); // barata (Postgres): sempre acompanha
      if (atHead) dispatch('fetchOutages');
    }, REFRESH_DEBOUNCE);
  },
};

const mutations = {
  // `de_manutencao` TAMBÉM: são os cards read-only da voz de manutenção na
  // página de Comunicados — engolir a chave aqui deixaria a IA falando de
  // manutenção com a página mostrando vazio (a divergência que ela audita).
  [types.SET_NOTICES]($state, notices) {
    $state.notices = {
      ativos: notices?.ativos || [],
      de_manutencao: notices?.de_manutencao || [],
      // Valor EFETIVO da janela da quarentena (o backend sempre manda —
      // setting da conta ou default). Alimenta o cadastro na página.
      config: notices?.config || null,
    };
  },

  [types.SET_UI_FLAG]($state, flags) {
    $state.uiFlags = { ...$state.uiFlags, ...flags };
  },
  [types.SET_SNAPSHOT]($state, snapshot) {
    $state.snapshot = snapshot;
  },
  [types.SET_OUTAGES]($state, outages) {
    $state.outages = outages;
  },
  [types.SET_OPEN_OUTAGES]($state, outages) {
    $state.openOutages = outages;
  },
  // Config confirmada pelo PATCH — entra sem esperar refetch (nova referência).
  [types.SET_NOTICE_CONFIG]($state, config) {
    $state.notices = { ...$state.notices, config };
  },
  [types.SET_META]($state, meta) {
    $state.meta = {
      totalCount: Number(meta.total_count || 0),
      page: Number(meta.current_page || 1),
      perPage: Number(meta.per_page || 25),
    };
  },
  [types.SET_TIMELINE]($state, timeline) {
    $state.timeline = timeline;
  },
  [types.SET_ERROR]($state, error) {
    $state.error = error;
  },
  [types.SET_FILTERS]($state, partial) {
    $state.filters = { ...$state.filters, ...partial };
  },
  [types.SET_CLIENTS]($state, { outageId, entry }) {
    // Nova referência (nunca mutação in-place): o drawer lê por getter.
    const next = { ...$state.clientsByOutage };
    if (entry) next[outageId] = entry;
    else delete next[outageId];
    $state.clientsByOutage = next;
  },
  [types.SET_MAINTENANCE_AUDIENCE]($state, { id, entry }) {
    // Nova referência (nunca mutação in-place): a árvore lê por getter.
    const next = { ...$state.audienceByMaintenance };
    if (entry) next[id] = entry;
    else delete next[id];
    $state.audienceByMaintenance = next;
  },
  [types.SET_SELECTED]($state, outageId) {
    $state.selectedOutageId = outageId;
  },
  [types.ADD_PENDING]($state) {
    $state.pendingEvents += 1;
  },
  [types.CLEAR_PENDING]($state) {
    $state.pendingEvents = 0;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
